import React, { useState } from 'react'
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { CheckCheck, Copy } from 'lucide-react';
import { BounceInDownButton } from '../../animation/bounceIn';
import { useToast } from '../../hooks/usetoast';
import { copyToClipboard } from '../../lib/copytoClipboard';
import { useTFASettings } from './api/apiSecuritysettings';
import { CircularProgress } from '@mui/material';
import { PageLoader } from '../../lib/loader';
import PropTypes from 'prop-types';


const schema = yup.object().shape({
    tfaCode: yup.string()
        .required('TFA code is Required')
        .matches(/\d{6}/, 'Invalid TFA code'),
    password: yup.string()
        .min(6, 'Password must be at least 6 characters')
        .max(30, 'Password must be at most 30 characters')
        .required('Password is Required'),
})

function TFASettings({ getTFAdata, isLoading }) {

    const toast = useToast();
    const tfaSettingsmutation = useTFASettings()
    const [iscopied, setIscopied] = useState(false);
    const [inputtype, setInputtype] = useState(true);

    const { control, handleSubmit, formState: { errors }, reset } = useForm({
        mode: 'onChange',
        resolver: yupResolver(schema),
        defaultValues: {
            tfaCode: "",
            password: "",
        }
    });

    const handleCopy = async () => {
        setIscopied(true)
        await copyToClipboard(getTFAdata?.data?.twoFactorSecret)

        toast.copy("Key copied");
        setTimeout(() => {
            setIscopied(false)
        }, 800)
    }

    const onSubmit = async (data) => {
        const sendObj = {
            password: data.password,
            status: getTFAdata?.data?.twoFactorEnabled ? 'disable' : 'enable',
            token: data.tfaCode
        }

        const res = await tfaSettingsmutation.mutateAsync(sendObj)
        if (res.status) reset()
    }
    const buttonText = getTFAdata?.data?.twoFactorEnabled ? "Disable" : "Enable";


    return (
            isLoading ? (
            <PageLoader />
            ) : (
            <BounceInDownButton>
                <div className="ad_pg_bx row justify-content-center">
                    <div className="col-sm-12 col-md-12 col-lg-8 col-xl-6">
                        <div className="TfaGrid">
                            <div className="TfaIcon">
                                <img
                                    src={require('../../assets/images/icons/profile-2user.svg').default}
                                    className="img-fluid"
                                    alt="icons"
                                />
                            </div>
                            <div className="TfaTxt">
                                <p>Two-factor authentication</p>
                            </div>
                        </div>

                        {!getTFAdata?.data?.twoFactorEnabled && (
                            <div className="row align-items-end mb-2">
                                <div className="col-sm-4 col-form-label text-center">
                                    {!getTFAdata?.data?.twoFactorQrCode ? (
                                        <div className='d-flex justify-content-center align-items-center' style={{ minHeight: "100px" }}>
                                            <CircularProgress style={{ color: "#000" }} size={30} />
                                        </div>
                                    ) : (
                                        <img src={getTFAdata?.data?.twoFactorQrCode} alt="qrCode" className='qrCode' />
                                    )}
                                </div>
                                <div className="col-sm-8">
                                    <p className='mb-0'>Your authentication key:</p>
                                    <div className="col-form-label pb-3 d-flex align-items-center">
                                        {getTFAdata?.data?.twoFactorSecret || "*******"}
                                        {getTFAdata?.data?.twoFactorSecret && (
                                            iscopied ? (
                                                <CheckCheck size={20} className='mx-2' />
                                            ) : (
                                                <Copy size={20} type='button' className='mx-2' onClick={handleCopy} />
                                            )
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}

                        <form onSubmit={handleSubmit(onSubmit)} noValidate>
                            <div className="row align-items-center mb-4">
                                <label className="col-sm-4 col-form-label" htmlFor="twofa">
                                    Enter 2FA Code
                                </label>
                                <div className="col-sm-8">
                                    <Controller name='tfaCode' control={control} render={({ field }) => (
                                        <input
                                            {...field}
                                            type="number"
                                            className="form-control"
                                            placeholder='Enter your 2FA code'
                                            autoComplete='off'
                                            onKeyDown={(e) => {
                                                if (['-', 'e', 'E', '+'].includes(e.key)) {
                                                    e.preventDefault();
                                                }
                                            }}
                                        />
                                    )} />
                                    {errors.tfaCode && <p className="errMsg">* {errors.tfaCode.message}</p>}
                                </div>
                            </div>

                            <div className="row align-items-center mb-4">
                                <label className="col-sm-4 col-form-label" htmlFor="passwordfield">
                                    Enter Password
                                </label>
                                <div className="col-sm-8">
                                    <div className="input-group">
                                        <Controller name='password' control={control} render={({ field }) => (
                                            <input
                                                {...field}
                                                type={inputtype ? "password" : "text"}
                                                className="form-control"
                                                placeholder='Enter your password'
                                                autoComplete='off'
                                                onKeyDown={(e) => {
                                                    if (['-', 'e', 'E', '+'].includes(e.key)) {
                                                        e.preventDefault();
                                                    }
                                                }}
                                            />
                                        )} />
                                        <button
                                            type="button"
                                            onClick={() => setInputtype(!inputtype)}
                                            className={`btn p-0 input-group-icon border-0 shwPwd ${inputtype ? "" : "show-eye"}`.trim()}
                                        >
                                            <img
                                                src={require('../../assets/images/icons/eye-slash.svg').default}
                                                className='img-fluid eye-hide'
                                                alt="eye-slash"
                                            />
                                            <img
                                                src={require('../../assets/images/icons/eye.svg').default}
                                                className='img-fluid eye-show'
                                                alt="eye"
                                            />
                                        </button>
                                    </div>
                                    {errors.password && <p className="errMsg">* {errors.password.message}</p>}
                                </div>
                            </div>

                            <div className="mb-3">
                                <button className="btn btn-primary btn-mw-132" disabled={tfaSettingsmutation.isLoading}>
                                    {tfaSettingsmutation.isLoading ? (
                                        <CircularProgress style={{ color: "#fff" }} size={20} />
                                    ) : (
                                        buttonText
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </BounceInDownButton>
            )
    );

}
TFASettings.propTypes = {
    getTFAdata: PropTypes.any.isRequired,
    isLoading: PropTypes.any.isRequired,

};
export default TFASettings